import * as React from 'react';
import PortalIdParser from 'PortalIdParser';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIResultsMessage from 'UIComponents/results/UIResultsMessage';
import useTimelineItemsWithFilter from 'flow-doctor-lib/hooks/useTimelineItemsWithFilter';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import SelectableEnrollmentTimeline from 'flow-doctor-lib/components/timeline/SelectableEnrollmentTimeline';
import useUpdateQueryParams from 'flow-doctor-lib/hooks/useUpdateQueryParams';
import useQueryParams, { nanNumberMapper } from 'flow-doctor-lib/hooks/useQueryParams';
import { makeTimelineItemUuid } from 'flow-doctor-lib/utils/timelineItemUtils';
import { H5 } from 'UIComponents/elements/headings';
import usageTracker from 'flow-doctor-lib/usageTracker';
import TimestampedMicroTimeRangeInput from 'flow-doctor-lib/components/common/TimestampedMicroTimeRangeInput';
import { getNDaysAgo } from 'flow-doctor-lib/utils/dateTimeUtils';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const VALID_TIMELINE_TRIGGER_TYPES = ['PROPERTY_CHANGE', 'FORM_SUBMISSION', 'LIST_MEMBERSHIP', 'LIST_MEMBERSHIP_WITH_VISIBILITY',
// 'OBJECT_MERGE',
'ASSOCIATION_CHANGE', 'ASSOCIATION_PROPERTY_CHANGE', 'UNIFIED_EVENT',
// 'FLOW_REVISION',
'ENROLLMENT_LOG_EVENT'];
const ChooseTimelineItemSection = ({
  flowId,
  objectId,
  objectTypeId,
  setSelectedTimelineItem
}) => {
  const updateQueryParams = useUpdateQueryParams();
  const {
    startTimestamp,
    endTimestamp,
    timelineItem: timelineItemUuid
  } = useQueryParams({
    startTimestamp: nanNumberMapper,
    endTimestamp: nanNumberMapper
  });

  // Ensure there is always a time range selected
  React.useEffect(() => {
    const paramsToUpdate = {};
    if (startTimestamp && endTimestamp) {
      return;
    }
    if (!startTimestamp || !endTimestamp) {
      paramsToUpdate.startTimestamp = getNDaysAgo(3, 'PORTAL_TZ');
      paramsToUpdate.endTimestamp = getNDaysAgo(0, 'PORTAL_TZ');
    }
    updateQueryParams(paramsToUpdate);
  }, [startTimestamp, endTimestamp, updateQueryParams]);
  const {
    timelineLogItems,
    timelineLoading,
    timelineError
  } = useTimelineItemsWithFilter({
    portalId: PortalIdParser.get(),
    flowId,
    objectId,
    objectTypeId,
    startTimestamp: startTimestamp,
    endTimestamp: endTimestamp,
    timelineTypes: VALID_TIMELINE_TRIGGER_TYPES
  }, {
    skip: !startTimestamp || !endTimestamp
  });

  // Auto-select the timeline item if UUID is present in the query params
  React.useEffect(() => {
    if (timelineItemUuid) {
      const matchingTimelineItem = timelineLogItems.find(item => makeTimelineItemUuid(item) === timelineItemUuid);
      if (matchingTimelineItem) {
        setSelectedTimelineItem(matchingTimelineItem);
      }
    }
  }, [timelineLogItems, timelineItemUuid, setSelectedTimelineItem]);
  const setStartTimestamp = timestamp => {
    if (endTimestamp) {
      usageTracker.track('pageAction', {
        actionType: 'SELECT_NON_ENROLLMENT_TIMEFRAME',
        objectTypeId
      });
    }
    updateQueryParams({
      startTimestamp: timestamp
    });
  };
  const setEndTimestamp = timestamp => {
    if (startTimestamp) {
      usageTracker.track('pageAction', {
        actionType: 'SELECT_NON_ENROLLMENT_TIMEFRAME',
        objectTypeId
      });
    }
    updateQueryParams({
      endTimestamp: timestamp
    });
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(H5, {
      "aria-level": 2,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "flow-doctor-lib.flows.diagnosticWizard.nonEnrollment.chooseTrigger.chooseAnEvent.title"
      })
    }), /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "flow-doctor-lib.flows.diagnosticWizard.nonEnrollment.chooseTrigger.chooseAnEvent.description_jsx",
      elements: {
        br: 'br'
      }
    }), /*#__PURE__*/_jsx(View, {
      marginBlock: "700",
      as: "div",
      children: /*#__PURE__*/_jsx(TimestampedMicroTimeRangeInput, {
        view: "STACKED",
        timezone: "PORTAL_TZ",
        startTimestamp: startTimestamp,
        endTimestamp: endTimestamp,
        onStartTimestampChange: setStartTimestamp,
        onEndTimestampChange: setEndTimestamp
      })
    }), timelineError ? /*#__PURE__*/_jsx(UIErrorMessage, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "flow-doctor-lib.flows.diagnosticWizard.nonEnrollment.chooseTrigger.error.title"
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "flow-doctor-lib.flows.diagnosticWizard.nonEnrollment.chooseTrigger.error.description"
      })
    }) : timelineLoading ? /*#__PURE__*/_jsx(UILoadingSpinner, {
      layout: "centered"
    }) : timelineLogItems.length > 0 ? /*#__PURE__*/_jsx(SelectableEnrollmentTimeline, {
      flowId: flowId,
      objectTypeId: objectTypeId,
      timelineLogItems: timelineLogItems,
      onItemSelect: setSelectedTimelineItem
    }) : /*#__PURE__*/_jsx(UIResultsMessage, {
      illustration: "empty-state-charts",
      illustrationProps: {
        width: 187
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "flow-doctor-lib.flows.diagnosticWizard.nonEnrollment.chooseTrigger.noEvents"
      })
    })]
  });
};
export default ChooseTimelineItemSection;